import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import MediaComponent from '../Media/media-component';

/**
 * GalleryLightbox
 * Full-screen image showcase with keyboard navigation.
 *
 * Props:
 *   images, array of { src, title }
 *   index, currently open image index (null = closed)
 *   onClose, called when lightbox should close
 *   onPrev, called to go to previous image
 *   onNext, called to go to next image
 */
const GalleryLightbox = ({ images, index, onClose, onPrev, onNext }) => {
    const isOpen = index !== null && index !== undefined;

    useEffect(() => {
        if (!isOpen) return;

        const onKey = (e) => {
            if (e.key === 'Escape')     onClose();
            if (e.key === 'ArrowLeft')  onPrev();
            if (e.key === 'ArrowRight') onNext();
        };
        window.addEventListener('keydown', onKey);

        document.body.style.overflow = 'hidden';

        return () => {
            window.removeEventListener('keydown', onKey);
            document.body.style.overflow = '';
        };
    }, [isOpen, onClose, onPrev, onNext]);

    if (!isOpen) return null;

    const current = images[index];

    return createPortal(
        <div
            className="fixed inset-0 z-[99999] bg-black/90 backdrop-blur-xl flex items-center justify-center p-4"
            onClick={onClose}
        >
            {/* Close */}
            <button
                className="absolute top-4 right-4 z-10 text-white/60 hover:text-white transition-colors p-2"
                onClick={onClose}
            >
                <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
            </button>

            {/* Prev */}
            {images.length > 1 && (
                <button
                    className="absolute left-4 top-1/2 -translate-y-1/2 z-10 text-white/60 hover:text-white transition-colors p-2"
                    onClick={(e) => { e.stopPropagation(); onPrev(); }}
                >
                    <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                    </svg>
                </button>
            )}

            {/* Image */}
            <div
                className="max-h-[90vh] max-w-[90vw]"
                onClick={(e) => e.stopPropagation()}
            >
                <MediaComponent
                    src={current?.src}
                    alt={current?.title || ''}
                    useSekeleton={false}
                    objectFit="contain"
                    wrapperClassName="max-h-[90vh] max-w-[90vw]"
                    className="max-h-[90vh] max-w-[90vw] object-contain rounded-lg"
                />
            </div>

            {/* Next */}
            {images.length > 1 && (
                <button
                    className="absolute right-4 top-1/2 -translate-y-1/2 z-10 text-white/60 hover:text-white transition-colors p-2"
                    onClick={(e) => { e.stopPropagation(); onNext(); }}
                >
                    <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                    </svg>
                </button>
            )}

            {/* Counter */}
            {images.length > 1 && (
                <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white/40 text-xs font-medium">
                    {index + 1} / {images.length}
                </div>
            )}
        </div>,
        document.body
    );
};

export default GalleryLightbox;
